import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { Alert, Muted } from '@/components/ui'
import { fetchCargos, fetchDepartamentos, type ColaboradorWritePayload } from '@/features/rh/api'
import { maskCpfInput, maskPhoneInput, onlyDigits, parseMoney } from '@/features/rh/utils'
import { createAdmissaoCompleta } from './api'
import {
  ADMISSAO_ETAPAS,
  BANCOS,
  BENEFICIOS_OPTS,
  DOCS_LISTA,
  NIVEIS_FORMACAO,
  UFS,
} from './admissaoConstants'

type DependenteRow = {
  id: string
  nome: string
  parentesco: string
  cpf: string
  data_nascimento: string
  ir: boolean
  plano_saude: boolean
}

type FormacaoRow = {
  id: string
  nivel: string
  curso: string
  instituicao: string
  conclusao: string
  status: string
}

type EmergenciaRow = {
  id: string
  nome: string
  telefone: string
  parentesco: string
  principal: boolean
}

type WizardDados = {
  nome: string
  cpf: string
  rg: string
  data_nascimento: string
  sexo: string
  estado_civil: string
  nacionalidade: string
  nome_pai: string
  nome_mae: string
  email: string
  celular: string
  telefone: string
  cep: string
  logradouro: string
  numero: string
  complemento: string
  bairro: string
  cidade: string
  estado: string
  data_admissao: string
  regime: string
  salario_base: string
  cargo_id: string
  departamento_id: string
  carga_horaria: string
  matricula: string
  email_corporativo: string
  pis_pasep: string
  ctps_numero: string
  ctps_serie: string
  ctps_uf: string
  banco: string
  agencia: string
  conta: string
  tipo_conta: string
  pix_tipo: string
  pix_chave: string
  pcd: boolean
  usa_epi: boolean
  observacoes: string
}

type Props = {
  onCancel: () => void
  onSuccess: (admissaoId: string, colaboradorId?: string) => void
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function emptyDados(): WizardDados {
  return {
    nome: '',
    cpf: '',
    rg: '',
    data_nascimento: '',
    sexo: '',
    estado_civil: '',
    nacionalidade: 'Brasileiro(a)',
    nome_pai: '',
    nome_mae: '',
    email: '',
    celular: '',
    telefone: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: 'PB',
    data_admissao: new Date().toISOString().slice(0, 10),
    regime: 'clt',
    salario_base: '',
    cargo_id: '',
    departamento_id: '',
    carga_horaria: '220',
    matricula: '',
    email_corporativo: '',
    pis_pasep: '',
    ctps_numero: '',
    ctps_serie: '',
    ctps_uf: 'PB',
    banco: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    pix_tipo: '',
    pix_chave: '',
    pcd: false,
    usa_epi: false,
    observacoes: '',
  }
}

export function AdmissaoWizard({ onCancel, onSuccess }: Props) {
  const [etapa, setEtapa] = useState(1)
  const [error, setError] = useState('')
  const [dados, setDados] = useState<WizardDados>(emptyDados)
  const [dependentes, setDependentes] = useState<DependenteRow[]>([])
  const [formacao, setFormacao] = useState<FormacaoRow[]>([])
  const [reservista, setReservista] = useState({
    numero: '',
    categoria: '',
    orgao: '',
    validade: '',
  })
  const [saude, setSaude] = useState({
    tipo_sanguineo: '',
    peso: '',
    altura: '',
    alergias: '',
    intolerancias: '',
    obs_medicas: '',
  })
  const [emergencia, setEmergencia] = useState<EmergenciaRow[]>([])
  const [documentos, setDocumentos] = useState<Record<string, boolean>>({})
  const [beneficios, setBeneficios] = useState<Record<string, boolean>>({})

  const catalogs = useQuery({
    queryKey: ['dp', 'admissao', 'catalogs'],
    queryFn: async () => {
      const [cargos, departamentos] = await Promise.all([
        fetchCargos({ completo: true, comColaboradores: true }),
        fetchDepartamentos(),
      ])
      return {
        cargos: cargos.dados || [],
        departamentos: departamentos || [],
      }
    },
  })

  const mut = useMutation({
    mutationFn: async () => {
      const salario = parseMoney(dados.salario_base)
      if (!dados.nome.trim()) throw new Error('Informe o nome completo.')
      const cpf = onlyDigits(dados.cpf)
      if (cpf.length !== 11) throw new Error('CPF inválido.')
      if (!dados.data_admissao) throw new Error('Informe a data de admissão.')
      if (Number.isNaN(salario)) throw new Error('Informe o salário base.')

      const colaborador: ColaboradorWritePayload = {
        nome: dados.nome.trim(),
        cpf,
        salario_base: salario,
        data_admissao: dados.data_admissao,
        status: 'admissao_pendente',
        regime: dados.regime || 'clt',
        rg: dados.rg.trim() || undefined,
        data_nascimento: dados.data_nascimento || null,
        sexo: dados.sexo || undefined,
        estado_civil: dados.estado_civil || undefined,
        nacionalidade: dados.nacionalidade || 'Brasileiro(a)',
        nome_pai: dados.nome_pai.trim() || undefined,
        nome_mae: dados.nome_mae.trim() || undefined,
        email: dados.email.trim() || undefined,
        celular: onlyDigits(dados.celular) || undefined,
        telefone: onlyDigits(dados.telefone) || undefined,
        cep: onlyDigits(dados.cep).slice(0, 8) || undefined,
        logradouro: dados.logradouro.trim() || undefined,
        numero: dados.numero.trim() || undefined,
        complemento: dados.complemento.trim() || undefined,
        bairro: dados.bairro.trim() || undefined,
        cidade: dados.cidade.trim() || undefined,
        estado: dados.estado.trim().toUpperCase().slice(0, 2) || undefined,
        cargo: dados.cargo_id || null,
        departamento: dados.departamento_id || null,
        carga_horaria: Number(dados.carga_horaria) || 220,
        matricula: dados.matricula.trim() || undefined,
        email_corporativo: dados.email_corporativo.trim() || undefined,
        pis_pasep: onlyDigits(dados.pis_pasep) || undefined,
        ctps_numero: dados.ctps_numero.trim() || undefined,
        ctps_serie: dados.ctps_serie.trim() || undefined,
        ctps_uf: dados.ctps_uf.trim().toUpperCase().slice(0, 2) || undefined,
        banco: dados.banco.trim() || undefined,
        agencia: dados.agencia.trim() || undefined,
        conta: dados.conta.trim() || undefined,
        tipo_conta: dados.tipo_conta || undefined,
        pix_tipo: dados.pix_tipo || undefined,
        pix_chave: dados.pix_chave.trim() || undefined,
        pcd: dados.pcd,
        observacoes: dados.observacoes.trim() || undefined,
        grau_instrucao: formacao[0]?.nivel || undefined,
      }

      const docsPayload: Record<string, boolean> = {}
      for (const d of DOCS_LISTA) docsPayload[d.id] = Boolean(documentos[d.id])

      return createAdmissaoCompleta({
        colaborador,
        dependentes: dependentes
          .filter((d) => d.nome.trim())
          .map((d) => ({
            nome: d.nome.trim(),
            parentesco: d.parentesco || 'Outro',
            cpf: onlyDigits(d.cpf),
            data_nascimento: d.data_nascimento || null,
            ir: d.ir,
            plano_saude: d.plano_saude,
          })),
        wizard: {
          formacao,
          reservista,
          saude,
          emergencia,
          documentos: docsPayload,
          beneficios,
          usa_epi: dados.usa_epi,
        },
        flags: {
          exame_admissional: Boolean(documentos.atestado_adm),
          epi_entregue: false,
        },
        observacoes: dados.observacoes.trim(),
      })
    },
    onSuccess: (res) => {
      if (res.dados?.id) onSuccess(res.dados.id, res.colaborador_id || res.dados.colaborador_id)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Falha ao salvar.')
    },
  })

  const pct = useMemo(() => Math.round((etapa / ADMISSAO_ETAPAS.length) * 100), [etapa])

  function setField<K extends keyof WizardDados>(key: K, value: WizardDados[K]) {
    setDados((d) => ({ ...d, [key]: value }))
  }

  function validarEtapa(): string | null {
    if (etapa === 1) {
      if (!dados.nome.trim()) return 'Informe o nome completo.'
      if (onlyDigits(dados.cpf).length !== 11) return 'CPF inválido.'
    }
    if (etapa === 2) {
      if (!dados.data_admissao) return 'Informe a data de admissão.'
      if (Number.isNaN(parseMoney(dados.salario_base))) return 'Informe o salário base.'
    }
    if (etapa === 8) {
      const faltando = DOCS_LISTA.filter((d) => d.req && !documentos[d.id])
      if (faltando.length) {
        return `Marque os documentos obrigatórios: ${faltando.map((d) => d.label).join(', ')}.`
      }
    }
    return null
  }

  function avancar() {
    setError('')
    const err = validarEtapa()
    if (err) {
      setError(err)
      return
    }
    if (etapa < ADMISSAO_ETAPAS.length) setEtapa((e) => e + 1)
  }

  function voltar() {
    setError('')
    if (etapa > 1) setEtapa((e) => e - 1)
  }

  function finalizar() {
    setError('')
    const err = validarEtapa()
    if (err) {
      setError(err)
      return
    }
    mut.mutate()
  }

  async function buscarCep() {
    const cep = onlyDigits(dados.cep)
    if (cep.length !== 8) return
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      const json = (await res.json()) as {
        erro?: boolean
        logradouro?: string
        bairro?: string
        localidade?: string
        uf?: string
      }
      if (json.erro) return
      setDados((d) => ({
        ...d,
        logradouro: json.logradouro || d.logradouro,
        bairro: json.bairro || d.bairro,
        cidade: json.localidade || d.cidade,
        estado: json.uf || d.estado,
      }))
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="card card-body hi-stack hi-admissao-wizard">
      <div className="hi-admissao-stepper" aria-label="Etapas da admissão">
        {ADMISSAO_ETAPAS.map((e) => (
          <button
            key={e.id}
            type="button"
            className={`hi-admissao-step${etapa === e.id ? ' is-active' : ''}${etapa > e.id ? ' is-done' : ''}`}
            onClick={() => setEtapa(e.id)}
          >
            <span className="hi-admissao-step-n">{etapa > e.id ? '✓' : e.id}</span>
            <span className="hi-admissao-step-l">{e.label}</span>
          </button>
        ))}
      </div>

      <div className="hi-admissao-progress">
        <Muted>
          Etapa {etapa} de {ADMISSAO_ETAPAS.length} — {ADMISSAO_ETAPAS[etapa - 1].label}
        </Muted>
        <div className="hi-pct-bar" title={`${pct}%`}>
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {etapa === 1 ? (
        <div className="hi-form-grid">
          <label className="hi-span-2">
            Nome completo *
            <input value={dados.nome} onChange={(e) => setField('nome', e.target.value)} required />
          </label>
          <label>
            CPF *
            <input
              value={dados.cpf}
              onChange={(e) => setField('cpf', maskCpfInput(e.target.value))}
              inputMode="numeric"
            />
          </label>
          <label>
            RG
            <input value={dados.rg} onChange={(e) => setField('rg', e.target.value)} />
          </label>
          <label>
            Nascimento
            <input
              type="date"
              value={dados.data_nascimento}
              onChange={(e) => setField('data_nascimento', e.target.value)}
            />
          </label>
          <label>
            Sexo
            <select value={dados.sexo} onChange={(e) => setField('sexo', e.target.value)}>
              <option value="">—</option>
              <option value="M">Masculino</option>
              <option value="F">Feminino</option>
              <option value="O">Outro</option>
            </select>
          </label>
          <label>
            Estado civil
            <select value={dados.estado_civil} onChange={(e) => setField('estado_civil', e.target.value)}>
              <option value="">—</option>
              <option value="solteiro">Solteiro(a)</option>
              <option value="casado">Casado(a)</option>
              <option value="divorc">Divorciado(a)</option>
              <option value="viuvo">Viúvo(a)</option>
              <option value="uniao">União estável</option>
            </select>
          </label>
          <label>
            Nacionalidade
            <input
              value={dados.nacionalidade}
              onChange={(e) => setField('nacionalidade', e.target.value)}
            />
          </label>
          <label>
            Nome do pai
            <input value={dados.nome_pai} onChange={(e) => setField('nome_pai', e.target.value)} />
          </label>
          <label>
            Nome da mãe
            <input value={dados.nome_mae} onChange={(e) => setField('nome_mae', e.target.value)} />
          </label>
          <label>
            E-mail pessoal
            <input
              type="email"
              value={dados.email}
              onChange={(e) => setField('email', e.target.value)}
            />
          </label>
          <label>
            Celular
            <input
              value={dados.celular}
              onChange={(e) => setField('celular', maskPhoneInput(e.target.value))}
            />
          </label>
          <label>
            Telefone
            <input
              value={dados.telefone}
              onChange={(e) => setField('telefone', maskPhoneInput(e.target.value))}
            />
          </label>
          <label>
            CEP
            <input
              value={dados.cep}
              onChange={(e) => setField('cep', e.target.value)}
              onBlur={() => void buscarCep()}
            />
          </label>
          <label className="hi-span-2">
            Logradouro
            <input value={dados.logradouro} onChange={(e) => setField('logradouro', e.target.value)} />
          </label>
          <label>
            Número
            <input value={dados.numero} onChange={(e) => setField('numero', e.target.value)} />
          </label>
          <label>
            Complemento
            <input
              value={dados.complemento}
              onChange={(e) => setField('complemento', e.target.value)}
            />
          </label>
          <label>
            Bairro
            <input value={dados.bairro} onChange={(e) => setField('bairro', e.target.value)} />
          </label>
          <label>
            Cidade
            <input value={dados.cidade} onChange={(e) => setField('cidade', e.target.value)} />
          </label>
          <label>
            UF
            <select value={dados.estado} onChange={(e) => setField('estado', e.target.value)}>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {etapa === 2 ? (
        <div className="hi-form-grid">
          <label>
            Data de admissão *
            <input
              type="date"
              value={dados.data_admissao}
              onChange={(e) => setField('data_admissao', e.target.value)}
            />
          </label>
          <label>
            Regime
            <select value={dados.regime} onChange={(e) => setField('regime', e.target.value)}>
              <option value="clt">CLT</option>
              <option value="pj">PJ</option>
              <option value="estagio">Estágio</option>
              <option value="temporario">Temporário</option>
              <option value="autonomo">Autônomo</option>
            </select>
          </label>
          <label>
            Salário base *
            <input
              value={dados.salario_base}
              onChange={(e) => setField('salario_base', e.target.value)}
              placeholder="Ex.: 2500,00"
            />
          </label>
          <label>
            Carga horária (mês)
            <input
              value={dados.carga_horaria}
              onChange={(e) => setField('carga_horaria', e.target.value)}
            />
          </label>
          <label>
            Cargo (ativos com colaboradores)
            <select
              value={dados.cargo_id}
              onChange={(e) => {
                const id = e.target.value
                const c = catalogs.data?.cargos.find((x) => x.id === id)
                setDados((d) => ({
                  ...d,
                  cargo_id: id,
                  departamento_id: d.departamento_id || c?.departamento_id || '',
                }))
              }}
            >
              <option value="">— cargo novo / sem vínculo —</option>
              {(catalogs.data?.cargos || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                  {c.headcount != null ? ` (${c.headcount})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Departamento
            <select
              value={dados.departamento_id}
              onChange={(e) => setField('departamento_id', e.target.value)}
            >
              <option value="">—</option>
              {(catalogs.data?.departamentos || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.nome}
                </option>
              ))}
            </select>
          </label>
          <label>
            Matrícula
            <input value={dados.matricula} onChange={(e) => setField('matricula', e.target.value)} />
          </label>
          <label>
            E-mail corporativo
            <input
              type="email"
              value={dados.email_corporativo}
              onChange={(e) => setField('email_corporativo', e.target.value)}
            />
          </label>
          <label>
            PIS/PASEP
            <input value={dados.pis_pasep} onChange={(e) => setField('pis_pasep', e.target.value)} />
          </label>
          <label>
            CTPS nº
            <input
              value={dados.ctps_numero}
              onChange={(e) => setField('ctps_numero', e.target.value)}
            />
          </label>
          <label>
            CTPS série
            <input value={dados.ctps_serie} onChange={(e) => setField('ctps_serie', e.target.value)} />
          </label>
          <label>
            CTPS UF
            <select value={dados.ctps_uf} onChange={(e) => setField('ctps_uf', e.target.value)}>
              {UFS.map((uf) => (
                <option key={uf} value={uf}>
                  {uf}
                </option>
              ))}
            </select>
          </label>
          <label>
            Banco
            <select value={dados.banco} onChange={(e) => setField('banco', e.target.value)}>
              <option value="">—</option>
              {BANCOS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </label>
          <label>
            Agência
            <input value={dados.agencia} onChange={(e) => setField('agencia', e.target.value)} />
          </label>
          <label>
            Conta
            <input value={dados.conta} onChange={(e) => setField('conta', e.target.value)} />
          </label>
          <label>
            Tipo de conta
            <select value={dados.tipo_conta} onChange={(e) => setField('tipo_conta', e.target.value)}>
              <option value="corrente">Corrente</option>
              <option value="poupanca">Poupança</option>
              <option value="salario">Salário</option>
            </select>
          </label>
          <label>
            Pix (tipo)
            <select value={dados.pix_tipo} onChange={(e) => setField('pix_tipo', e.target.value)}>
              <option value="">—</option>
              <option value="cpf">CPF</option>
              <option value="email">E-mail</option>
              <option value="telefone">Telefone</option>
              <option value="aleatoria">Aleatória</option>
            </select>
          </label>
          <label>
            Chave Pix
            <input value={dados.pix_chave} onChange={(e) => setField('pix_chave', e.target.value)} />
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={dados.usa_epi}
              onChange={(e) => setField('usa_epi', e.target.checked)}
            />
            Função exige EPI
          </label>
        </div>
      ) : null}

      {etapa === 3 ? (
        <div className="hi-stack">
          <Muted>Dependentes para IRRF / plano de saúde (opcional).</Muted>
          {dependentes.map((d, idx) => (
            <div key={d.id} className="hi-form-grid card card-body">
              <label>
                Nome
                <input
                  value={d.nome}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, nome: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label>
                Parentesco
                <input
                  value={d.parentesco}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, parentesco: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label>
                CPF
                <input
                  value={d.cpf}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, cpf: maskCpfInput(e.target.value) } : x)),
                    )
                  }
                />
              </label>
              <label>
                Nascimento
                <input
                  type="date"
                  value={d.data_nascimento}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, data_nascimento: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={d.ir}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, ir: e.target.checked } : x)),
                    )
                  }
                />
                Dependente IRRF
              </label>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={d.plano_saude}
                  onChange={(e) =>
                    setDependentes((list) =>
                      list.map((x, i) => (i === idx ? { ...x, plano_saude: e.target.checked } : x)),
                    )
                  }
                />
                Plano de saúde
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setDependentes((list) => list.filter((_, i) => i !== idx))}
              >
                Remover
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setDependentes((list) => [
                ...list,
                {
                  id: uid(),
                  nome: '',
                  parentesco: '',
                  cpf: '',
                  data_nascimento: '',
                  ir: false,
                  plano_saude: false,
                },
              ])
            }
          >
            + Dependente
          </button>
        </div>
      ) : null}

      {etapa === 4 ? (
        <div className="hi-stack">
          {formacao.map((f, idx) => (
            <div key={f.id} className="hi-form-grid card card-body">
              <label>
                Nível
                <select
                  value={f.nivel}
                  onChange={(e) =>
                    setFormacao((list) =>
                      list.map((x, i) => (i === idx ? { ...x, nivel: e.target.value } : x)),
                    )
                  }
                >
                  <option value="">—</option>
                  {NIVEIS_FORMACAO.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Curso
                <input
                  value={f.curso}
                  onChange={(e) =>
                    setFormacao((list) =>
                      list.map((x, i) => (i === idx ? { ...x, curso: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label>
                Instituição
                <input
                  value={f.instituicao}
                  onChange={(e) =>
                    setFormacao((list) =>
                      list.map((x, i) => (i === idx ? { ...x, instituicao: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label>
                Conclusão
                <input
                  value={f.conclusao}
                  onChange={(e) =>
                    setFormacao((list) =>
                      list.map((x, i) => (i === idx ? { ...x, conclusao: e.target.value } : x)),
                    )
                  }
                  placeholder="Ano ou data"
                />
              </label>
              <label>
                Status
                <select
                  value={f.status}
                  onChange={(e) =>
                    setFormacao((list) =>
                      list.map((x, i) => (i === idx ? { ...x, status: e.target.value } : x)),
                    )
                  }
                >
                  <option value="Concluído">Concluído</option>
                  <option value="Em Andamento">Em andamento</option>
                  <option value="Trancado">Trancado</option>
                </select>
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setFormacao((list) => list.filter((_, i) => i !== idx))}
              >
                Remover
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setFormacao((list) => [
                ...list,
                { id: uid(), nivel: '', curso: '', instituicao: '', conclusao: '', status: 'Concluído' },
              ])
            }
          >
            + Formação
          </button>
        </div>
      ) : null}

      {etapa === 5 ? (
        <div className="hi-form-grid">
          <label>
            Nº reservista
            <input
              value={reservista.numero}
              onChange={(e) => setReservista((r) => ({ ...r, numero: e.target.value }))}
            />
          </label>
          <label>
            Categoria
            <select
              value={reservista.categoria}
              onChange={(e) => setReservista((r) => ({ ...r, categoria: e.target.value }))}
            >
              <option value="">—</option>
              <option value="1ª Categoria">1ª Categoria</option>
              <option value="2ª Categoria">2ª Categoria</option>
              <option value="3ª Categoria">3ª Categoria</option>
              <option value="Isento">Isento</option>
            </select>
          </label>
          <label>
            Órgão expedidor
            <input
              value={reservista.orgao}
              onChange={(e) => setReservista((r) => ({ ...r, orgao: e.target.value }))}
            />
          </label>
          <label>
            Validade
            <input
              type="date"
              value={reservista.validade}
              onChange={(e) => setReservista((r) => ({ ...r, validade: e.target.value }))}
            />
          </label>
        </div>
      ) : null}

      {etapa === 6 ? (
        <div className="hi-form-grid">
          <label>
            Tipo sanguíneo
            <select
              value={saude.tipo_sanguineo}
              onChange={(e) => setSaude((s) => ({ ...s, tipo_sanguineo: e.target.value }))}
            >
              <option value="">—</option>
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Peso (kg)
            <input
              value={saude.peso}
              onChange={(e) => setSaude((s) => ({ ...s, peso: e.target.value }))}
            />
          </label>
          <label>
            Altura (m)
            <input
              value={saude.altura}
              onChange={(e) => setSaude((s) => ({ ...s, altura: e.target.value }))}
            />
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={dados.pcd}
              onChange={(e) => setField('pcd', e.target.checked)}
            />
            PcD / cota
          </label>
          <label className="hi-span-2">
            Alergias
            <input
              value={saude.alergias}
              onChange={(e) => setSaude((s) => ({ ...s, alergias: e.target.value }))}
            />
          </label>
          <label className="hi-span-2">
            Intolerâncias
            <input
              value={saude.intolerancias}
              onChange={(e) => setSaude((s) => ({ ...s, intolerancias: e.target.value }))}
            />
          </label>
          <label className="hi-span-2">
            Observações médicas
            <textarea
              rows={3}
              value={saude.obs_medicas}
              onChange={(e) => setSaude((s) => ({ ...s, obs_medicas: e.target.value }))}
            />
          </label>
        </div>
      ) : null}

      {etapa === 7 ? (
        <div className="hi-stack">
          {emergencia.map((em, idx) => (
            <div key={em.id} className="hi-form-grid card card-body">
              <label>
                Nome
                <input
                  value={em.nome}
                  onChange={(e) =>
                    setEmergencia((list) =>
                      list.map((x, i) => (i === idx ? { ...x, nome: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label>
                Telefone
                <input
                  value={em.telefone}
                  onChange={(e) =>
                    setEmergencia((list) =>
                      list.map((x, i) =>
                        i === idx ? { ...x, telefone: maskPhoneInput(e.target.value) } : x,
                      ),
                    )
                  }
                />
              </label>
              <label>
                Parentesco
                <input
                  value={em.parentesco}
                  onChange={(e) =>
                    setEmergencia((list) =>
                      list.map((x, i) => (i === idx ? { ...x, parentesco: e.target.value } : x)),
                    )
                  }
                />
              </label>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={em.principal}
                  onChange={(e) =>
                    setEmergencia((list) =>
                      list.map((x, i) => ({
                        ...x,
                        principal: i === idx ? e.target.checked : false,
                      })),
                    )
                  }
                />
                Contato principal
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setEmergencia((list) => list.filter((_, i) => i !== idx))}
              >
                Remover
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              setEmergencia((list) => [
                ...list,
                { id: uid(), nome: '', telefone: '', parentesco: '', principal: list.length === 0 },
              ])
            }
          >
            + Contato de emergência
          </button>
        </div>
      ) : null}

      {etapa === 8 ? (
        <ul className="hi-checklist">
          {DOCS_LISTA.map((doc) => (
            <li key={doc.id}>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={Boolean(documentos[doc.id])}
                  onChange={(e) =>
                    setDocumentos((d) => ({ ...d, [doc.id]: e.target.checked }))
                  }
                />
                <span>
                  {doc.label}
                  {doc.req ? ' *' : ''}
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}

      {etapa === 9 ? (
        <ul className="hi-checklist">
          {BENEFICIOS_OPTS.map((b) => (
            <li key={b.id}>
              <label className="hi-check">
                <input
                  type="checkbox"
                  checked={Boolean(beneficios[b.id])}
                  onChange={(e) =>
                    setBeneficios((d) => ({ ...d, [b.id]: e.target.checked }))
                  }
                />
                <span>{b.label}</span>
              </label>
            </li>
          ))}
        </ul>
      ) : null}

      {etapa === 10 ? (
        <div className="hi-stack">
          <Alert tone="info">
            Revise os dados. Ao confirmar, o colaborador é criado na API RH e o checklist de
            onboarding DP é iniciado.
          </Alert>
          <ul className="hi-meta">
            <li>
              <strong>{dados.nome || '—'}</strong>
            </li>
            <li>CPF {dados.cpf || '—'}</li>
            <li>Admissão {dados.data_admissao || '—'}</li>
            <li>Regime {(dados.regime || 'clt').toUpperCase()}</li>
            <li>Salário {dados.salario_base || '—'}</li>
            <li>{dependentes.filter((d) => d.nome.trim()).length} dependente(s)</li>
            <li>
              Docs obrigatórios:{' '}
              {DOCS_LISTA.filter((d) => d.req && documentos[d.id]).length}/
              {DOCS_LISTA.filter((d) => d.req).length}
            </li>
          </ul>
          <label className="hi-span-2">
            Observações finais
            <textarea
              rows={3}
              value={dados.observacoes}
              onChange={(e) => setField('observacoes', e.target.value)}
            />
          </label>
        </div>
      ) : null}

      <div className="hi-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancelar
        </button>
        {etapa > 1 ? (
          <button type="button" className="btn btn-secondary" onClick={voltar} disabled={mut.isPending}>
            Voltar
          </button>
        ) : null}
        {etapa < ADMISSAO_ETAPAS.length ? (
          <button type="button" className="btn btn-primary" onClick={avancar}>
            Próximo
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            disabled={mut.isPending}
            onClick={finalizar}
          >
            {mut.isPending ? 'Registrando…' : 'Confirmar admissão'}
          </button>
        )}
      </div>
    </div>
  )
}
